import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {catchError, Observable, throwError, shareReplay, finalize, switchMap} from 'rxjs';
import {Router} from '@angular/router';
import {SecurityService} from '../services/security.service';
import {NotificationService} from '../../notifications/notification.service';
import {NotificationType} from '../../notifications/notification.model';
import { APP_ROUTES } from 'src/app/app-routing.const';
import { notify } from '../../utils/function/notification.util';

@Injectable()
export class SecurityInterceptor implements HttpInterceptor {

  constructor(
    private router: Router,
    private securityService: SecurityService,
    private notificationService: NotificationService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.securityService.isLoggedIn() && req.url.includes('/api/v1/') && !req.url.includes('/api/v1/auth')) {
      this.router.navigate([APP_ROUTES.AUTH.LOGIN]);
      console.warn('Unauthenticated request, redirecting to login...')
    }

    const token = this.securityService.getToken();
    let authReq = req;
    if (req.url.includes('/api/v1/auth')) {
      authReq = authReq.clone({ withCredentials: true });
    }
    if (token !== null) {
      authReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`)
      });
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (req.headers.has('X-Skip-Interceptor')) {
          return throwError(() => error);
        }
        if (error.status === 401) {
          return this.refreshToken().pipe(
            switchMap(() => next.handle(this.withAuth(req))),
            catchError(err => {
              notify(this.notificationService, NotificationType.ERROR, 'application.unauthenticated');
              this.securityService.clearSession();
              this.router.navigate([APP_ROUTES.AUTH.LOGIN]);
              return throwError(() => err);
            })
          );
        }
        notify(this.notificationService, NotificationType.ERROR, error.status + ': ' + error.message);
        return throwError(() => error);
      })
    );
  }

  private refresh$: Observable<any> | null = null;

  private refreshToken(): Observable<any> {
    if (!this.refresh$) {
      this.refresh$ = this.securityService.refreshSession().pipe(
        shareReplay(1),
        finalize(() => this.refresh$ = null)
      );
    }
    return this.refresh$;
  }

  private withAuth(req: HttpRequest<any>): HttpRequest<any> {
    const token = this.securityService.getToken();
    return token ? req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) }) : req;
  }
}
